import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2 } from "lucide-react";

export default function AdminUsers() {
  const { t } = useLang();
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "secretary", village: "K. Gopalapuram" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", form);
      toast.success(t("user_created"));
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "secretary", village: "K. Gopalapuram" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/admin/users/${u.user_id}`, { active: !u.active });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/users/${deleteTarget.user_id}`);
      toast.success(t("user_deleted"));
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-users-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl tracking-tight font-semibold text-graphite" data-testid="users-title">{t("users_title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("users_subtitle")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-user-btn" className="bg-brand-red hover:bg-brand-reddark text-white transition-colors duration-200">
              <Plus className="h-4 w-4 mr-2" />{t("create_user")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("create_user")}</DialogTitle>
              <DialogDescription>{t("no_public_signup")}</DialogDescription>
            </DialogHeader>
            <form onSubmit={createUser} className="space-y-4">
              <div className="space-y-1.5">
                <Label>{t("u_name")}</Label>
                <Input data-testid="user-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>{t("u_email")}</Label>
                <Input data-testid="user-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>{t("u_password")}</Label>
                <Input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="(Google login only if empty)" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{t("u_role")}</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">{t("role_admin")}</SelectItem>
                      <SelectItem value="secretary">{t("role_secretary")}</SelectItem>
                      <SelectItem value="demo">{t("role_demo")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.role === "secretary" && (
                  <div className="space-y-1.5">
                    <Label>{t("u_village")}</Label>
                    <Input data-testid="user-village-input" value={form.village} onChange={(e) => setForm({ ...form, village: e.target.value })} />
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button data-testid="user-create-submit" type="submit" className="bg-brand-red hover:bg-brand-reddark text-white">{t("create")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs">{t("u_name")}</TableHead>
              <TableHead className="text-xs">{t("u_email")}</TableHead>
              <TableHead className="text-xs">{t("u_role")}</TableHead>
              <TableHead className="text-xs">{t("u_village")}</TableHead>
              <TableHead className="text-xs">{t("status_label")}</TableHead>
              <TableHead className="text-xs text-right">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users?.map((u) => (
              <TableRow key={u.user_id} data-testid={`user-row-${u.email}`}>
                <TableCell className="text-sm font-medium text-graphite">{u.name}</TableCell>
                <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs capitalize">{t(`role_${u.role}`)}</Badge></TableCell>
                <TableCell className="text-xs text-slate-500">{u.village || "—"}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch
                      data-testid={`user-active-${u.email}`}
                      checked={u.active !== false}
                      disabled={u.user_id === me?.user_id}
                      onCheckedChange={() => toggleActive(u)}
                    />
                    <span className="text-xs text-slate-500">{u.active !== false ? t("active") : t("inactive")}</span>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    data-testid={`user-delete-${u.email}`}
                    variant="ghost" size="icon" className="h-8 w-8"
                    disabled={u.user_id === me?.user_id}
                    onClick={() => setDeleteTarget(u)}
                  >
                    <Trash2 className="h-4 w-4 text-brand-red" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete")} — {deleteTarget?.email}?</AlertDialogTitle>
            <AlertDialogDescription>{t("delete_confirm_desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="user-delete-confirm" className="bg-brand-red hover:bg-brand-reddark text-white" onClick={doDelete}>
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
